import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Dialog } from "primereact/dialog";
import { CustomTable } from "../../componentes/table/CustomTable";
import Button from "../../componentes/button/button";
import CardIndicador from "../../components/solicitacoes/Shared/CardIndicador";
import ContasAPagarService from "../../services/resource/contasAPagarService";
import ContasPagarEmAberto from "../../components/solicitacoes/ContasAPagar/ContasPagarEmAberto";
import ConciliacaoExtrato from "../../components/solicitacoes/ContasAPagar/ConciliacaoExtrato";
import LancamentoFortesCard, { Info, situacaoPagamento } from "../../components/solicitacoes/Shared/LancamentoFortesCard";
import { processoInfo } from "../../utils/solicitacoes/processoRegistry";
import { formatarData, formatarMoeda } from "../../utils/solicitacoes/formatters";
import { navegarComCtrl } from "../../utils/solicitacoes/navegacao";

const service = new ContasAPagarService();

const ABAS = [
    { chave: "solicitacoes", label: "Solicitações em Pagamento", icone: "fa fa-tasks" },
    { chave: "em-aberto", label: "Contas a Pagar em Aberto", icone: "fa fa-file-invoice-dollar" },
    { chave: "extrato", label: "Conciliação com extrato", icone: "fa fa-university" },
];

const STATUS_INFO = {
    ENCONTRADA: { label: "Encontrada", className: "bg-success/10 text-success", icone: "fa fa-check-circle" },
    PROVAVEL: { label: "Provável", className: "bg-amber-100 text-amber-700", icone: "fa fa-question-circle" },
    NAO_ENCONTRADA: { label: "Não encontrada", className: "bg-destructive/10 text-destructive", icone: "fa fa-times-circle" },
    SEM_FORNECEDOR: { label: "Sem fornecedor", className: "bg-muted text-muted-foreground", icone: "fa fa-user-slash" },
    FORTES_INDISPONIVEL: { label: "Fortes indisponível", className: "bg-muted text-muted-foreground", icone: "fa fa-plug" },
};

function StatusConciliacao({ status }) {
    const info = STATUS_INFO[status] || { label: status, className: "bg-muted text-foreground", icone: "fa fa-circle" };
    return (
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap ${info.className}`}>
            <i className={info.icone} /> {info.label}
        </span>
    );
}

// Paga = todas as parcelas quitadas no Fortes ou débito do extrato bancário vinculado à solicitação.
function estaPaga(item) {
    return situacaoPagamento(item.lancamentosFortes) === "PAGO" || !!item.extratoDataPagamento;
}

function PagamentoFortes({ situacao }) {
    if (!situacao) return <span className="text-muted-foreground">-</span>;
    if (situacao === "PAGO") {
        return <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold bg-success/10 text-success"><i className="fa fa-check" /> Pago</span>;
    }
    if (situacao === "EM_ABERTO") {
        return <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold bg-muted text-muted-foreground">Em aberto</span>;
    }
    return <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold bg-amber-100 text-amber-700">Parcial {situacao.split(":")[1]}</span>;
}

// Somente visualização, por enquanto: solicitações paradas na Confirmação de Pagamento e o
// lançamento correspondente encontrado no contas a pagar do Fortes (ver ContasAPagarService no backend).
export default function ContasAPagar() {
    const navigate = useNavigate();
    const [itens, setItens] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [filtroStatus, setFiltroStatus] = useState(null);
    // "TODAS" | "PAGAS" | "NAO_PAGAS" — paga = quitada no Fortes ou vinculada a um débito do extrato
    const [filtroPagamento, setFiltroPagamento] = useState("TODAS");
    const [vencimentoDe, setVencimentoDe] = useState("");
    const [vencimentoAte, setVencimentoAte] = useState("");
    const [selecionado, setSelecionado] = useState(null);
    const [marcados, setMarcados] = useState([]);
    const [confirmandoLote, setConfirmandoLote] = useState(false);
    const [comentarioLote, setComentarioLote] = useState("");
    const [finalizando, setFinalizando] = useState(false);
    const [resultadoLote, setResultadoLote] = useState(null);
    const [aba, setAba] = useState("solicitacoes");

    // Ctrl/Cmd+clique abre a solicitação em outra aba; clique simples mostra o lançamento do Fortes.
    function aoClicarLinha(row, e) {
        if (e?.ctrlKey || e?.metaKey) {
            window.open(`/solicitacoes/${row.solicitacaoId}`, "_blank", "noopener,noreferrer");
            return;
        }
        setSelecionado(row);
    }

    function finalizarLote() {
        setFinalizando(true);
        service.finalizar(marcados, comentarioLote.trim() || null)
            .then(res => {
                const resultados = res.data;
                const falhas = resultados.filter(r => !r.sucesso);
                const sucessos = resultados.length - falhas.length;
                if (sucessos > 0) toast.success(`${sucessos} pagamento(s) confirmado(s).`);
                if (falhas.length > 0) setResultadoLote(falhas);
                setConfirmandoLote(false);
                setComentarioLote("");
                setMarcados([]);
                carregar();
            })
            .catch(error => {
                console.error("Erro ao finalizar pagamentos em lote", error);
                toast.error("Não foi possível finalizar os pagamentos.");
            })
            .finally(() => setFinalizando(false));
    }

    function carregar() {
        setCarregando(true);
        service.listar()
            .then(res => setItens(res.data))
            .catch(error => {
                console.error("Erro ao carregar contas a pagar", error);
                toast.error("Erro ao carregar as contas a pagar");
            })
            .finally(() => setCarregando(false));
    }

    useEffect(() => {
        document.title = "MyDash | Contas a Pagar";
        carregar();
    }, []);

    // Período de vencimento da solicitação (datas ISO yyyy-MM-dd, comparáveis como texto). Os indicadores
    // acompanham o período; a seleção de status/pagamento só restringe a lista.
    const itensNoPeriodo = useMemo(() => itens.filter(i => {
        if (!vencimentoDe && !vencimentoAte) return true;
        if (!i.vencimento) return false;
        const venc = String(i.vencimento).slice(0, 10);
        return (!vencimentoDe || venc >= vencimentoDe) && (!vencimentoAte || venc <= vencimentoAte);
    }), [itens, vencimentoDe, vencimentoAte]);

    const contagem = useMemo(() => itensNoPeriodo.reduce((acc, i) => ({ ...acc, [i.statusConciliacao]: (acc[i.statusConciliacao] || 0) + 1 }), {}), [itensNoPeriodo]);

    const totalPagas = useMemo(() => itensNoPeriodo.filter(estaPaga).length, [itensNoPeriodo]);

    const linhas = useMemo(() => itensNoPeriodo
        .filter(i => !filtroStatus || i.statusConciliacao === filtroStatus)
        .filter(i => filtroPagamento === "TODAS" || (filtroPagamento === "PAGAS") === estaPaga(i))
        .map(i => ({
            ...i,
            processoLabel: processoInfo(i.tipoProcesso)?.label || i.tipoProcesso,
            notasTxt: i.notasFiscais?.join(", ") || "-",
            fortesCodigos: i.lancamentosFortes?.map(l => l.codigo).join(", ") || "-",
            fortesDocumentos: i.lancamentosFortes?.map(l => l.numeroDocumento).filter(Boolean).join(", ") || "-",
            fortesValor: i.lancamentosFortes?.length === 1 ? i.lancamentosFortes[0].valor : null,
            pagamentoFortes: situacaoPagamento(i.lancamentosFortes),
        })), [itensNoPeriodo, filtroStatus, filtroPagamento]);

    // Só conta o que ainda está na lista (após Atualizar, o que já foi finalizado sai dela).
    const itensMarcados = itens.filter(i => marcados.includes(i.solicitacaoId));
    const valorMarcado = itensMarcados.reduce((soma, i) => soma + (Number(i.valor) || 0), 0);

    const colunas = [
        {
            key: "numero", label: "Número", width: 100,
            render: (v, row) => (
                <button type="button" className="text-primary font-semibold hover:underline"
                    onClick={e => { e.stopPropagation(); navegarComCtrl(navigate, `/solicitacoes/${row.solicitacaoId}`, e); }}>
                    {v}
                </button>
            ),
        },
        { key: "processoLabel", label: "Processo" },
        { key: "filialNome", label: "Filial" },
        { key: "fornecedorNome", label: "Fornecedor" },
        { key: "valor", label: "Valor", format: "currency" },
        { key: "vencimento", label: "Vencimento", format: "date" },
        { key: "notasTxt", label: "Nota(s) Fiscal(is)" },
        { key: "statusConciliacao", label: "Fortes", center: true, render: v => <StatusConciliacao status={v} /> },
        { key: "fortesCodigos", label: "Código Fortes" },
        { key: "fortesDocumentos", label: "Documento Fortes" },
        { key: "fortesValor", label: "Valor Fortes", render: v => v != null ? formatarMoeda(v) : "-" },
        { key: "pagamentoFortes", label: "Pago no Fortes", center: true, render: v => <PagamentoFortes situacao={v} /> },
        { key: "extratoDataPagamento", label: "Pago no extrato", format: "date", center: true },
        { key: "criterioConciliacao", label: "Critério" },
        { key: "responsavelNome", label: "Responsável" },
    ];

    const kpis = [
        { chave: null, label: "Em pagamento", valor: itensNoPeriodo.length, icon: "fa fa-file-invoice-dollar", iconClassName: "bg-primary/10 text-primary" },
        { chave: "ENCONTRADA", label: "Encontradas no Fortes", valor: contagem.ENCONTRADA || 0, icon: "fa fa-check-circle", iconClassName: "bg-success/10 text-success" },
        { chave: "PROVAVEL", label: "Prováveis (por valor)", valor: contagem.PROVAVEL || 0, icon: "fa fa-question-circle", iconClassName: "bg-amber-100 text-amber-700" },
        { chave: "NAO_ENCONTRADA", label: "Não encontradas", valor: contagem.NAO_ENCONTRADA || 0, icon: "fa fa-times-circle", iconClassName: "bg-destructive/10 text-destructive" },
    ];

    return (
        <main className="px-4 py-6">
            <div className="mx-auto flex flex-col gap-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="font-display text-2xl font-bold text-foreground">Contas a Pagar</h1>
                        <p className="text-sm text-muted-foreground">
                            {aba === "solicitacoes"
                                ? "Lançamentos de Despesa e Solicitações de Compras na etapa de Pagamento, com o lançamento correspondente no Fortes."
                                : aba === "extrato"
                                    ? "Vincule os débitos do extrato bancário às solicitações aguardando pagamento."
                                    : "Parcelas ainda não pagas no Fortes, pelo vencimento."}
                        </p>
                    </div>
                    {aba === "solicitacoes" && (
                        <Button variant="outline" label="Atualizar" iconLeft="fa fa-rotate" loading={carregando} onClick={carregar} />
                    )}
                </div>

                <div className="flex items-center gap-1.5 border-b border-border">
                    {ABAS.map(a => (
                        <button
                            key={a.chave}
                            type="button"
                            onClick={() => setAba(a.chave)}
                            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${aba === a.chave
                                ? "border-primary text-primary"
                                : "border-transparent text-muted-foreground hover:text-foreground"}`}
                        >
                            <i className={a.icone} /> {a.label}
                        </button>
                    ))}
                </div>

                {aba === "extrato" ? <ConciliacaoExtrato aoAlterar={carregar} /> : aba === "em-aberto" ? <ContasPagarEmAberto /> : (<>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                    {kpis.map(k => (
                        <CardIndicador
                            key={k.label}
                            label={k.label}
                            value={k.valor}
                            icon={k.icon}
                            iconClassName={k.iconClassName}
                            loading={carregando}
                            active={filtroStatus === k.chave && filtroPagamento === "TODAS"}
                            onClick={() => { setFiltroStatus(k.chave); setFiltroPagamento("TODAS"); }}
                        />
                    ))}
                    <CardIndicador
                        label="Pagas (Fortes ou extrato)"
                        value={totalPagas}
                        icon="fa fa-circle-check"
                        iconClassName="bg-success/10 text-success"
                        loading={carregando}
                        active={filtroPagamento === "PAGAS"}
                        onClick={() => { setFiltroStatus(null); setFiltroPagamento(filtroPagamento === "PAGAS" ? "TODAS" : "PAGAS"); }}
                    />
                </div>

                <div className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-card px-4 py-3">
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-muted-foreground">Vencimento de</label>
                        <input type="date" value={vencimentoDe} max={vencimentoAte || undefined}
                            onChange={e => setVencimentoDe(e.target.value)}
                            className="px-2 py-1.5 border border-input rounded-lg text-sm bg-card text-foreground" />
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-muted-foreground">até</label>
                        <input type="date" value={vencimentoAte} min={vencimentoDe || undefined}
                            onChange={e => setVencimentoAte(e.target.value)}
                            className="px-2 py-1.5 border border-input rounded-lg text-sm bg-card text-foreground" />
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-muted-foreground">Pagamento</label>
                        <select value={filtroPagamento} onChange={e => setFiltroPagamento(e.target.value)}
                            className="px-2 py-1.5 border border-input rounded-lg text-sm bg-card text-foreground">
                            <option value="TODAS">Todas</option>
                            <option value="PAGAS">Pagas (Fortes ou extrato)</option>
                            <option value="NAO_PAGAS">Não pagas</option>
                        </select>
                    </div>
                    {(vencimentoDe || vencimentoAte || filtroPagamento !== "TODAS" || filtroStatus) && (
                        <Button variant="outline" label="Limpar filtros" iconLeft="fa fa-eraser"
                            onClick={() => { setVencimentoDe(""); setVencimentoAte(""); setFiltroPagamento("TODAS"); setFiltroStatus(null); }} />
                    )}
                    <span className="text-xs text-muted-foreground ml-auto pb-2">
                        {linhas.length} de {itens.length} solicitação(ões)
                    </span>
                </div>

                {itensMarcados.length > 0 && (
                    <div className="sticky top-2 z-20 flex items-center justify-between flex-wrap gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-2.5 shadow-card">
                        <span className="text-sm text-foreground">
                            <strong>{itensMarcados.length}</strong> selecionada(s) · total <strong>{formatarMoeda(valorMarcado)}</strong>
                        </span>
                        <div className="flex gap-2">
                            <Button variant="outline" label="Limpar seleção" onClick={() => setMarcados([])} />
                            <Button variant="green" label={`Confirmar pagamento (${itensMarcados.length})`} iconLeft="fa fa-check-double"
                                onClick={() => setConfirmandoLote(true)} />
                        </div>
                    </div>
                )}

                <div className="bg-card border border-border rounded-lg shadow-card">
                    <CustomTable
                        data={linhas}
                        columns={colunas}
                        loading={carregando}
                        showHeader={false}
                        className="text-[11px] cursor-pointer"
                        pagination
                        pageSize={20}
                        exportable
                        exportFilename="contas_a_pagar"
                        onRowSelect={aoClicarLinha}
                        selectable
                        rowKey="solicitacaoId"
                        selectedKeys={marcados}
                        onSelectionChange={setMarcados}
                        isRowSelectable={row => row.podeFinalizar}
                    />
                </div>
                <p className="text-xs text-muted-foreground -mt-2">
                    <i className="fa fa-info-circle mr-1" />
                    Clique na linha para ver o lançamento no Fortes; Ctrl/Cmd+clique abre a solicitação em outra aba.
                    Só é possível selecionar as tarefas que estão com você ou com um grupo seu.
                </p>
                </>)}
            </div>

            <Dialog
                header={selecionado ? `Solicitação ${selecionado.numero} — ${selecionado.fornecedorNome || ""}` : ""}
                visible={!!selecionado}
                onHide={() => setSelecionado(null)}
                blockScroll
                maximizable
                className="w-[95vw] max-w-5xl"
            >
                {selecionado && <DetalheConciliacao item={selecionado} onAbrir={() => window.open(`/solicitacoes/${selecionado.solicitacaoId}`, "_blank", "noopener,noreferrer")} />}
            </Dialog>

            <Dialog
                header="Confirmar pagamentos"
                visible={confirmandoLote}
                onHide={() => !finalizando && setConfirmandoLote(false)}
                blockScroll
                className="w-[95vw] max-w-lg"
                footer={
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" label="Cancelar" disabled={finalizando} onClick={() => setConfirmandoLote(false)} />
                        <Button variant="green" label="Confirmar" iconLeft="fa fa-check-double" loading={finalizando} onClick={finalizarLote} />
                    </div>
                }
            >
                <div className="flex flex-col gap-3 pt-2 text-sm">
                    <p>
                        Concluir a etapa de Pagamento de <strong>{itensMarcados.length}</strong> solicitação(ões),
                        no total de <strong>{formatarMoeda(valorMarcado)}</strong>, como <strong>pagamento realizado e sem pendência</strong>?
                        As solicitações serão finalizadas.
                    </p>
                    <p className="text-xs text-muted-foreground">
                        Para registrar uma pendência (ex.: sem nota), abra a solicitação e use a etapa de Pagamento normalmente.
                    </p>
                    <div className="flex flex-col gap-1.5">
                        <label className="text-sm font-medium text-foreground/80">Comentário (opcional)</label>
                        <textarea
                            rows={2}
                            value={comentarioLote}
                            onChange={e => setComentarioLote(e.target.value)}
                            className="w-full px-3 py-2 border border-input rounded-lg text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
                        />
                    </div>
                </div>
            </Dialog>

            <Dialog
                header="Algumas solicitações não foram finalizadas"
                visible={!!resultadoLote}
                onHide={() => setResultadoLote(null)}
                blockScroll
                className="w-[95vw] max-w-lg"
            >
                <ul className="flex flex-col gap-2 pt-2 text-sm">
                    {resultadoLote?.map(r => (
                        <li key={r.solicitacaoId} className="flex gap-2">
                            <i className="fa fa-times-circle text-destructive mt-0.5" />
                            <span><strong>{r.numero || r.solicitacaoId}</strong>: {r.mensagem}</span>
                        </li>
                    ))}
                </ul>
            </Dialog>
        </main>
    );
}

function DetalheConciliacao({ item, onAbrir }) {
    return (
        <div className="flex flex-col gap-4 pt-2">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm rounded-lg bg-muted px-4 py-3">
                <Info label="Valor" valor={formatarMoeda(item.valor)} />
                <Info label="Vencimento" valor={formatarData(item.vencimento)} />
                <Info label="Nota(s) fiscal(is)" valor={item.notasFiscais?.join(", ") || "-"} />
                <Info label="CNPJ/CPF fornecedor" valor={item.fornecedorDocumento || "-"} />
            </div>

            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 text-sm">
                    <StatusConciliacao status={item.statusConciliacao} />
                    <span className="text-muted-foreground">{item.criterioConciliacao}</span>
                </div>
                <Button variant="outline" label="Abrir solicitação" iconLeft="fa fa-arrow-up-right-from-square" onClick={onAbrir} />
            </div>

            {item.lancamentosFortes?.length > 1 && (
                <p className="text-xs text-amber-700">
                    <i className="fa fa-exclamation-triangle mr-1" />
                    {item.lancamentosFortes.length} lançamentos no Fortes atendem ao critério — confira qual corresponde a esta solicitação.
                </p>
            )}

            {item.lancamentosFortes?.map(l => <LancamentoFortesCard key={l.codigo} lancamento={l} />)}
        </div>
    );
}
